import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Edit2, Trash2 } from "lucide-react";
import { useReservationCategories } from "@/hooks/reservations/useReservationCategories";
import { useReservationItems } from "@/hooks/reservations/useReservationItems";
import { useCreateReservationItem } from "@/hooks/reservations/useCreateReservationItem";
import { useUpdateReservationItem } from "@/hooks/reservations/useUpdateReservationItem";
import { useDeleteReservationItem } from "@/hooks/reservations/useDeleteReservationItem";
import { Badge } from "@/components/ui/badge";

interface ItemManagerProps {
  gymId: string;
}

export const ItemManager = ({ gymId }: ItemManagerProps) => {
  const [isCreating, setIsCreating] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [newItem, setNewItem] = useState({
    name: "",
    description: "",
    price_per_hour: "",
    category_id: "",
  });
  const [editItem, setEditItem] = useState({
    name: "",
    description: "",
    price_per_hour: "",
  });

  const { data: categories } = useReservationCategories(gymId);
  const { data: items } = useReservationItems(selectedCategoryId);
  const { mutate: createItem, isPending } = useCreateReservationItem();
  const { mutate: updateItem, isPending: isUpdating } = useUpdateReservationItem();
  const { mutate: deleteItem, isPending: isDeleting } = useDeleteReservationItem();

  const handleCreate = () => {
    if (!newItem.name.trim() || !newItem.category_id || !newItem.price_per_hour) return;

    createItem({
      name: newItem.name.trim(),
      description: newItem.description.trim() || undefined,
      price_per_hour: parseFloat(newItem.price_per_hour),
      category_id: newItem.category_id,
    }, {
      onSuccess: () => {
        setNewItem({ name: "", description: "", price_per_hour: "", category_id: "" });
        setIsCreating(false);
      },
    });
  };

  const handleCancel = () => {
    setNewItem({ name: "", description: "", price_per_hour: "", category_id: "" });
    setIsCreating(false);
  };

  const handleEdit = (item: any) => {
    setEditingItemId(item.id);
    setEditItem({
      name: item.name,
      description: item.description || "",
      price_per_hour: item.price_per_hour.toString(),
    });
  };

  const handleUpdate = () => {
    if (!editingItemId || !editItem.name.trim() || !editItem.price_per_hour) return;

    updateItem({
      id: editingItemId,
      name: editItem.name.trim(),
      description: editItem.description.trim() || undefined,
      price_per_hour: parseFloat(editItem.price_per_hour),
    }, {
      onSuccess: () => {
        setEditingItemId(null);
        setEditItem({ name: "", description: "", price_per_hour: "" });
      },
    });
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setEditItem({ name: "", description: "", price_per_hour: "" });
  };

  const handleDelete = (itemId: string) => {
    deleteItem(itemId);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="text-md font-medium">Elementos de Reserva</h4>
        <Button
          onClick={() => setIsCreating(true)}
          size="sm"
          disabled={isCreating || !categories?.length}
        >
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Elemento
        </Button>
      </div>

      {categories?.length === 0 && (
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground">
              Primero debes crear al menos una categoría para poder agregar elementos.
            </p>
          </CardContent>
        </Card>
      )}

      {isCreating && categories && categories.length > 0 && (
        <Card>
          <CardContent className="p-4 space-y-4">
            <Select
              value={newItem.category_id}
              onValueChange={(value) => setNewItem(prev => ({ ...prev, category_id: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecciona una categoría" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Input
              placeholder="Nombre del elemento (ej: Cancha de Fútbol 1)"
              value={newItem.name}
              onChange={(e) => setNewItem(prev => ({ ...prev, name: e.target.value }))}
            />
            
            <Input
              type="number"
              step="0.01"
              placeholder="Precio por hora"
              value={newItem.price_per_hour}
              onChange={(e) => setNewItem(prev => ({ ...prev, price_per_hour: e.target.value }))}
            />
            
            <Textarea
              placeholder="Descripción (opcional)"
              value={newItem.description}
              onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
            />
            
            <div className="flex gap-2">
              <Button
                onClick={handleCreate}
                disabled={!newItem.name.trim() || !newItem.category_id || !newItem.price_per_hour || isPending}
                size="sm"
              >
                {isPending ? "Creando..." : "Crear"}
              </Button>
              <Button
                variant="outline"
                onClick={handleCancel}
                size="sm"
              >
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {categories && categories.length > 0 && (
        <div className="space-y-4">
          <Select
            value={selectedCategoryId || ""}
            onValueChange={(value) => setSelectedCategoryId(value || null)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecciona una categoría para ver sus elementos" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((category) => (
                <SelectItem key={category.id} value={category.id}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {selectedCategoryId && items && (
            <div className="grid gap-4">
              {items.map((item) => (
                <Card key={item.id}>
                  <CardHeader className="pb-3">
                    {editingItemId === item.id ? (
                      <div className="space-y-4">
                        <Input
                          placeholder="Nombre del elemento"
                          value={editItem.name}
                          onChange={(e) => setEditItem(prev => ({ ...prev, name: e.target.value }))}
                        />
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Precio por hora"
                          value={editItem.price_per_hour}
                          onChange={(e) => setEditItem(prev => ({ ...prev, price_per_hour: e.target.value }))}
                        />
                        <Textarea
                          placeholder="Descripción (opcional)"
                          value={editItem.description}
                          onChange={(e) => setEditItem(prev => ({ ...prev, description: e.target.value }))}
                        />
                        <div className="flex gap-2">
                          <Button
                            onClick={handleUpdate}
                            disabled={!editItem.name.trim() || !editItem.price_per_hour || isUpdating}
                            size="sm"
                          >
                            {isUpdating ? "Guardando..." : "Guardar"}
                          </Button>
                          <Button
                            variant="outline"
                            onClick={handleCancelEdit}
                            size="sm"
                          >
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-lg">{item.name}</CardTitle>
                          {item.description && (
                            <p className="text-sm text-muted-foreground mt-1">
                              {item.description}
                            </p>
                          )}
                          <p className="text-sm font-medium mt-2">
                            ${item.price_per_hour}/hora
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Badge variant="secondary">
                            {item.is_active ? "Activo" : "Inactivo"}
                          </Badge>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleEdit(item)}
                            disabled={isUpdating || isDeleting}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                disabled={isUpdating || isDeleting}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Confirmar eliminación</AlertDialogTitle>
                                <AlertDialogDescription>
                                  ¿Estás seguro de que quieres eliminar "{item.name}"? Esta acción no se puede deshacer.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDelete(item.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  {isDeleting ? "Eliminando..." : "Eliminar"}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    )}
                  </CardHeader>
                </Card>
              ))}

              {items.length === 0 && (
                <Card>
                  <CardContent className="p-8 text-center">
                    <p className="text-muted-foreground">
                      No hay elementos en esta categoría. Crea el primer elemento.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};